import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Calendar as CalendarIcon, ChevronUp, ChevronDown, Copy, Sparkles, Undo2, TriangleAlert, OctagonAlert } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { ComboBox } from "@/shared/components/ComboBox/ComboBox";
import { renderTaskItem, renderActivityItem, renderMatterItem, renderSelectedMatter } from "./pickerItems";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  NarrativeTextArea,
  type NarrativeTextAreaRef,
  type Abbreviation,
} from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import { useRestrictedWords, useSettingsCache, useMattersCache } from "@/shared/cache";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import {
  detectRestrictedWords,
  mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import { type SearchableField } from "@/shared/utils/smartSearch";
import {
  isActivityRequired,
  isTaskRequired,
  isNamanHowell,
  getFirmDefaults,
} from "@/shared/utils/firm";
import {
  isZurichMatter,
  isValidUnitsForMatter,
  snapUnitsForMatter,
  stepUnitsUp,
  stepUnitsDown,
  ZURICH_UNITS_ERROR,
  STANDARD_UNITS_ERROR,
} from "@/shared/utils/zurich";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";

import {
  timeEntrySchema,
  type TimeEntryFormValues,
  defaultFormValues,
} from "./schema";
import { RestrictedWordDialog } from "./RestrictedWordDialog";

// ── Types ──────────────────────────────────────────────────────────

type Matter = {
  matter: string;
  matter_id: string;
  client: string;
  description: string;
  task_group: string;
  activity_group: string;
};

type Task = {
  task: string;
  task_description: string;
  task_group: string;
};

type Activity = {
  activity: string;
  activity_description: string;
  activity_group: string;
};

export type EntryFormProps = {
  matters: Matter[];
  tasks: Task[];
  activities: Activity[];
  currentDate: Date;
  defaultValues?: Partial<TimeEntryFormValues>;
  onSubmit: (values: TimeEntryFormValues) => void;
  /** Called when "Add and export" is clicked (validates then calls back) */
  onExport?: (values: TimeEntryFormValues) => void;
  /** Called when Duplicate is clicked (validates, saves, keeps form open) */
  onDuplicate?: (values: TimeEntryFormValues) => void;
  onCancel?: () => void;
  submitLabel?: string;
  /** Label for the export button (default: "Add and export") */
  exportLabel?: string;
  hardcodedMatter?: string;
  lastUsedMatter?: string;
  /** Read-only document/task title shown above matter (edit mode) */
  documentName?: string;
  /** Abbreviations that auto-expand on space */
  abbreviations?: Abbreviation[];
  /** Validate on mount — shows red borders for missing required fields (used by edit dialog for incomplete entries) */
  validateOnMount?: boolean;
};

// ── Component ──────────────────────────────────────────────────────

export function EntryForm({
  matters,
  tasks,
  activities,
  currentDate,
  defaultValues: defaultValuesProp,
  onSubmit,
  onExport,
  onDuplicate,
  onCancel,
  submitLabel = "Add entry",
  exportLabel = "Add and export",
  hardcodedMatter,
  lastUsedMatter,
  documentName,
  abbreviations,
  validateOnMount = false,
}: EntryFormProps) {
  const narrativeRef = React.useRef<NarrativeTextAreaRef>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  // Focus the form on mount so the first Tab lands on the Matter input
  React.useEffect(() => {
    const id = setTimeout(() => formRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, []);
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    getValues,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm<TimeEntryFormValues>({
    resolver: zodResolver(timeEntrySchema),
    defaultValues: {
      ...defaultFormValues,
      date: currentDate || new Date(),
      ...defaultValuesProp,
    },
  });

  const matterId = watch("matter_id");
  const taskValue = watch("task");
  const activityValue = watch("activity");
  const duration = watch("duration");

  // Clear validation errors when watched fields change
  React.useEffect(() => {
    if (errors.matter_id && matterId) clearErrors("matter_id");
  }, [matterId]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (errors.duration) clearErrors("duration");
  }, [duration]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (errors.task) clearErrors("task");
  }, [taskValue]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (errors.activity) clearErrors("activity");
  }, [activityValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restricted words (reactive to current matter) ─────────────

  const { getRestrictedWords } = useRestrictedWords();
  const { settings } = useSettingsCache();

  const restrictedWords = React.useMemo<RestrictedWord[]>(() => {
    if (!settings.check_for_red_words) return [];
    if (!matterId) return getRestrictedWords();
    const clientCode = matterId.split('.')[0];
    const matterCode = matterId.split('.').slice(1).join('.');
    return getRestrictedWords(clientCode, matterCode);
  }, [settings.check_for_red_words, matterId, getRestrictedWords]);

  // ── Derived data ───────────────────────────────────────────────

  const { isLoading: mattersLoading } = useMattersCache();

  const taskRequired = React.useMemo(
    () => mattersLoading ? true : isTaskRequired(matterId, matters),
    [matterId, matters, mattersLoading],
  );

  const activityRequired = React.useMemo(
    () => mattersLoading ? true : isActivityRequired(matterId, matters),
    [matterId, matters, mattersLoading],
  );

  // Validate on mount — highlights missing required fields when editing incomplete entries.
  // Deferred until matters have loaded so taskRequired/activityRequired reflect settled data.
  const hasValidatedOnMount = React.useRef(false);
  React.useEffect(() => {
    if (!validateOnMount || mattersLoading || hasValidatedOnMount.current) return;
    hasValidatedOnMount.current = true;
    trigger(["matter_id", "duration", "description"]);
    if (taskRequired && !getValues("task")) {
      setError("task", { type: "manual", message: "Task is required" });
    }
    if (activityRequired && !getValues("activity")) {
      setError("activity", { type: "manual", message: "Activity is required" });
    }
  }, [mattersLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const firmDefaults = getFirmDefaults();

  const availableTasks = React.useMemo(() => {
    const selected = matters.find((m) => m.matter_id === matterId);

    if (firmDefaults.taskGroup) {
      if (!selected || !selected.task_group?.trim()) {
        return tasks.filter((t) => t.task_group === firmDefaults.taskGroup);
      }
      return tasks.filter((t) => t.task_group === selected.task_group);
    }

    // No default configured — filter by matter's group if it has one, otherwise show all
    if (selected?.task_group?.trim()) {
      return tasks.filter((t) => t.task_group === selected.task_group);
    }
    return tasks;
  }, [tasks, matters, matterId, firmDefaults.taskGroup]);

  // Deduplicate, filter Z-codes, sort L-codes first by number ascending
  const uniqueTasks = React.useMemo(() => {
    const seen = new Set<string>();
    const result = availableTasks.filter((t) => {
      if (t.task.startsWith("Z") || t.task.startsWith("z")) return false;
      if (t.task_description.toLowerCase().startsWith("do not use")) return false;
      const key = `${t.task}-${t.task_description}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    result.sort((a, b) => {
      const aIsL = a.task.startsWith("L");
      const bIsL = b.task.startsWith("L");
      if (aIsL && !bIsL) return -1;
      if (!aIsL && bIsL) return 1;
      const aNum = parseInt(a.task.replace(/^\D+/, ""), 10) || 0;
      const bNum = parseInt(b.task.replace(/^\D+/, ""), 10) || 0;
      return aNum - bNum;
    });

    return result;
  }, [availableTasks]);

  // Show only L-codes by default; others appear when searching
  const taskDefaultLimit = React.useMemo(() => {
    const lCodeCount = uniqueTasks.filter((t) => t.task.startsWith("L")).length;
    return lCodeCount || undefined;
  }, [uniqueTasks]);

  const availableActivities = React.useMemo(() => {
    const selected = matters.find((m) => m.matter_id === matterId);

    if (firmDefaults.activityGroup) {
      if (!selected || !selected.activity_group?.trim()) {
        return activities.filter((a) => a.activity_group === firmDefaults.activityGroup);
      }
      return activities.filter((a) => a.activity_group === selected.activity_group);
    }

    // No default configured — filter by matter's group if it has one, otherwise show all
    if (selected?.activity_group?.trim()) {
      return activities.filter((a) => a.activity_group === selected.activity_group);
    }
    return activities;
  }, [activities, matters, matterId, firmDefaults.activityGroup]);

  // Deduplicate, filter Z-codes, sort A-codes first by number ascending
  const uniqueActivities = React.useMemo(() => {
    const seen = new Set<string>();
    const result = availableActivities.filter((a) => {
      if (a.activity.startsWith("Z") || a.activity.startsWith("z")) return false;
      if (a.activity_description.toLowerCase().startsWith("do not use")) return false;
      const key = `${a.activity}-${a.activity_description}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    result.sort((a, b) => {
      const aIsA = a.activity.startsWith("A");
      const bIsA = b.activity.startsWith("A");
      if (aIsA && !bIsA) return -1;
      if (!aIsA && bIsA) return 1;
      const aNum = parseInt(a.activity.replace(/^\D+/, ""), 10) || 0;
      const bNum = parseInt(b.activity.replace(/^\D+/, ""), 10) || 0;
      return aNum - bNum;
    });

    return result;
  }, [availableActivities]);

  // Show only A-codes by default; others appear when searching
  const activityDefaultLimit = React.useMemo(() => {
    const aCodeCount = uniqueActivities.filter((a) => a.activity.startsWith("A")).length;
    return aCodeCount || undefined;
  }, [uniqueActivities]);

  const setCustomError = React.useCallback(
    (field: keyof TimeEntryFormValues, message: string) => {
      setError(field, { type: "manual", message });
    },
    [setError],
  );

  // ── Duration helpers ─────────────────────────────────────────

  const selectedClient = React.useMemo(
    () => matters.find((m) => m.matter_id === matterId)?.client ?? "",
    [matters, matterId],
  );

  const handleDurationIncrement = React.useCallback(() => {
    const current = typeof duration === "number" ? duration : parseFloat(String(duration)) || 0;
    setValue("duration", stepUnitsUp(current, selectedClient));
  }, [duration, selectedClient, setValue]);

  const handleDurationDecrement = React.useCallback(() => {
    const current = typeof duration === "number" ? duration : parseFloat(String(duration)) || 0;
    setValue("duration", stepUnitsDown(current, selectedClient));
  }, [duration, selectedClient, setValue]);

  // ── AI generation ─────────────────────────────────────────────

  const { makeApiRequest } = useApi();
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [previousDescription, setPreviousDescription] = React.useState<string | null>(null);
  const streamingRef = React.useRef<{ cancelled: boolean; intervalId: ReturnType<typeof setInterval> } | null>(null);

  const description = watch("description");

  // Derive restricted-word match state locally so inline banners are
  // synchronous with the current render (no stale-ref lag).
  // Skip detection while streaming to avoid ~20 recalculations/sec.
  const localMatches = React.useMemo(
    () =>
      restrictedWords.length && !streamingRef.current
        ? mergeOverlappingMatches(detectRestrictedWords(description ?? "", restrictedWords))
        : [],
    [description, restrictedWords],
  );
  const localHasBlocking = localMatches.some((m) => m.severity === "block");
  const localHasWarning = localMatches.some((m) => m.severity === "warning");

  const handleGenerateDescription = React.useCallback(async () => {
    if (streamingRef.current) return;

    const currentDescription = getValues("description");
    if (currentDescription.trim().length < 20) {
      toast.error("Please enter a longer description.", {
      });
      return;
    }

    const descriptionBeforeGeneration = currentDescription;
    setIsGenerating(true);

    try {
      const generatedDescription = await logger.trackOperation(
        "AI narrative generation",
        async () => {
          const response = await makeApiRequest({
            endpoint: "/records/generateNewDescription",
            method: "POST",
            body: {
              description: currentDescription,
              date: getValues("date"),
            },
          });

          if (response.error) {
            throw new Error(response.error);
          }

          if (
            !response.data ||
            typeof response.data !== "string" ||
            !response.data.trim()
          ) {
            throw new Error("Invalid response from AI generation");
          }

          return response.data;
        },
        { inputLength: currentDescription.length },
        3000,
      );

      setPreviousDescription(descriptionBeforeGeneration);

      // Stream word-by-word for a typing effect
      const tokens = generatedDescription.split(/(\s+)/); // preserves whitespace
      let currentIndex = 0;
      const streaming = { cancelled: false, intervalId: 0 as unknown as ReturnType<typeof setInterval> };

      streaming.intervalId = setInterval(() => {
        if (streaming.cancelled || currentIndex >= tokens.length) {
          clearInterval(streaming.intervalId);
          streamingRef.current = null;
          if (!streaming.cancelled) {
            setValue("description", generatedDescription, { shouldDirty: true });
            setIsGenerating(false);
            toast.success("Description generated with AI!", {
                  });
          }
          return;
        }
        currentIndex += 1;
        // Skip whitespace-only tokens so visible text advances every tick
        while (currentIndex < tokens.length && /^\s+$/.test(tokens[currentIndex])) {
          currentIndex += 1;
        }
        const partialText = tokens.slice(0, currentIndex).join("");
        setValue("description", partialText, { shouldDirty: true });
      }, 50);

      streamingRef.current = streaming;
      // Don't set isGenerating(false) here — streaming callback handles it
    } catch (error) {
      logger.error("AI narrative generation failed", {
        error: error instanceof Error ? error.message : String(error),
        descriptionLength: getValues("description").length,
      });
      if (error instanceof Error) {
        logger.captureException(error, { action: "generateDescription" });
      }
      toast.error("Failed to generate description. Please try manually.", {
        duration: 5000,
      });
      setIsGenerating(false);
    }
  }, [getValues, setValue, makeApiRequest]);

  // Cancel any active streaming on unmount
  React.useEffect(() => {
    return () => {
      if (streamingRef.current) {
        streamingRef.current.cancelled = true;
        clearInterval(streamingRef.current.intervalId);
      }
    };
  }, []);

  const handleUndoGeneration = React.useCallback(() => {
    // Cancel streaming if in progress
    if (streamingRef.current) {
      streamingRef.current.cancelled = true;
      clearInterval(streamingRef.current.intervalId);
      streamingRef.current = null;
      setIsGenerating(false);
    }
    if (previousDescription !== null) {
      setValue("description", previousDescription, { shouldDirty: true });
      setPreviousDescription(null);
      toast.success("Reverted to previous narrative", {
      });
    }
  }, [previousDescription, setValue]);

  // ── Matter change handler ──────────────────────────────────────

  const handleMatterSelect = React.useCallback(
    (newMatterId: string) => {
      const selected = matters.find((m) => m.matter_id === newMatterId);
      if (!selected) {
        logger.warn("handleMatterSelect: matter not found", {
          searchedMatterId: newMatterId,
          mattersCount: matters.length,
        });
        return;
      }

      setValue("matter_id", selected.matter_id);
      setValue("matter", selected.matter);

      // Clear task/activity if not required for new matter
      if (!isTaskRequired(newMatterId, matters)) {
        setValue("task", "");
      }
      if (!isActivityRequired(newMatterId, matters) && !isNamanHowell()) {
        setValue("activity", "");
      }

      // Snap duration to the new matter's grid (handles Zurich → standard and back)
      const currentDuration = getValues("duration");
      if (typeof currentDuration === "number" && currentDuration > 0) {
        setValue("duration", snapUnitsForMatter(currentDuration, selected.client));
      }
    },
    [matters, matterId, setValue, getValues],
  );

  // ── Set hardcoded matter on mount ──────────────────────────────

  const didAutoPopulateLastUsed = React.useRef(false);
  React.useEffect(() => {
    const matterToUse = hardcodedMatter || (didAutoPopulateLastUsed.current ? undefined : lastUsedMatter);
    if (!matterToUse || matters.length === 0) return;
    // Mark as attempted as soon as matters are available, whether or not the
    // ID is found — prevents a later cache update from overwriting a manual pick.
    if (!hardcodedMatter) didAutoPopulateLastUsed.current = true;
    const selected = matters.find((m) => m.matter_id === matterToUse);
    if (selected) {
      setValue("matter_id", selected.matter_id);
      setValue("matter", selected.matter);
    }
  }, [hardcodedMatter, lastUsedMatter, matters, setValue]);

  // ── Search field configs ───────────────────────────────────────

  const matterFields: ((item: Matter) => SearchableField)[] = React.useMemo(
    () => [
      (m: Matter) => ({ value: m.matter, weight: 1.5 }),
      (m: Matter) => ({ value: m.client, weight: 1.2 }),
      (m: Matter) => ({ value: m.description, weight: 1.0 }),
    ],
    [],
  );

  const taskFields: ((item: Task) => SearchableField)[] = React.useMemo(
    () => [
      (t: Task) => ({ value: t.task, weight: 1.5 }),
      (t: Task) => ({ value: t.task_description, weight: 1.0 }),
    ],
    [],
  );

  const activityFields: ((item: Activity) => SearchableField)[] = React.useMemo(
    () => [
      (a: Activity) => ({ value: a.activity, weight: 1.5 }),
      (a: Activity) => ({ value: a.activity_description, weight: 1.0 }),
    ],
    [],
  );

  // ── Restricted word confirmation state ─────────────────────────

  const [restrictedWordDialogOpen, setRestrictedWordDialogOpen] = React.useState(false);
  const pendingActionRef = React.useRef<((values: TimeEntryFormValues) => void) | null>(null);
  const pendingValuesRef = React.useRef<TimeEntryFormValues | null>(null);

  // ── Form submit with conditional validation ────────────────────

  /** Shared validation + cleanup. Returns prepared values or null if invalid. */
  const validateAndPrepare = React.useCallback(
    (values: TimeEntryFormValues): TimeEntryFormValues | null => {
      // Validate conditional required fields
      if (taskRequired && !values.task) {
        setCustomError("task", "Task is required");
        logger.warn("Entry validation failed - missing task", {
          matterId: values.matter_id,
          taskRequired,
        });
        return null;
      }
      if (activityRequired && !values.activity) {
        setCustomError("activity", "Activity is required");
        logger.warn("Entry validation failed - missing activity", {
          matterId: values.matter_id,
          activityRequired,
        });
        return null;
      }

      // Validate unit increment against the matter's grid
      const selectedMatter = matters.find((m) => m.matter_id === values.matter_id);
      const submitClient = selectedMatter?.client ?? "";
      if (!isValidUnitsForMatter(values.duration, submitClient)) {
        const message = isZurichMatter(submitClient) ? ZURICH_UNITS_ERROR : STANDARD_UNITS_ERROR;
        setCustomError("duration", message);
        logger.warn("Entry validation failed - invalid unit increment", {
          duration: values.duration,
          isZurich: isZurichMatter(submitClient),
        });
        return null;
      }

      // Format date
      const prepared = { ...values };
      prepared.date = prepared.date instanceof Date ? prepared.date : new Date(prepared.date);

      // Clear optional fields
      if (!taskRequired) prepared.task = "";
      if (!activityRequired && !isNamanHowell()) prepared.activity = "";

      return prepared;
    },
    [taskRequired, activityRequired, matters, setCustomError],
  );

  /** Validate, check for restricted words, then either submit directly or show confirmation. */
  const submitWithRestrictedWordCheck = React.useCallback(
    (values: TimeEntryFormValues, action: (v: TimeEntryFormValues) => void) => {
      const prepared = validateAndPrepare(values);
      if (!prepared) return;

      const hasBlocking = narrativeRef.current?.hasBlockingWords();
      const hasWarnings = narrativeRef.current?.hasWarningWords();

      if (hasBlocking || hasWarnings) {
        // Open RestrictedWordDialog for both blocks and warnings
        pendingActionRef.current = action;
        pendingValuesRef.current = prepared;
        setRestrictedWordDialogOpen(true);
        return;
      }

      // No matches → proceed
      action(prepared);
    },
    [validateAndPrepare],
  );

  const handleRestrictedWordCancel = React.useCallback(() => {
    setRestrictedWordDialogOpen(false);
    pendingActionRef.current = null;
    pendingValuesRef.current = null;
  }, []);

  /** Called when the user manually fixes all blocking words and clicks Save in the dialog. */
  const handleRestrictedWordSave = React.useCallback(
    (updatedNarrative: string) => {
      setValue("description", updatedNarrative, { shouldDirty: true });
      if (pendingActionRef.current && pendingValuesRef.current) {
        pendingActionRef.current({
          ...pendingValuesRef.current,
          description: updatedNarrative,
        });
      }
      setRestrictedWordDialogOpen(false);
      pendingActionRef.current = null;
      pendingValuesRef.current = null;
    },
    [setValue],
  );

  const onFormSubmit = React.useCallback(
    (values: TimeEntryFormValues) => {
      submitWithRestrictedWordCheck(values, onSubmit);
    },
    [submitWithRestrictedWordCheck, onSubmit],
  );

  const onFormExport = React.useCallback(
    (values: TimeEntryFormValues) => {
      if (!onExport) return;
      submitWithRestrictedWordCheck(values, onExport);
    },
    [submitWithRestrictedWordCheck, onExport],
  );

  const onFormDuplicate = React.useCallback(
    (values: TimeEntryFormValues) => {
      if (!onDuplicate) return;
      submitWithRestrictedWordCheck(values, onDuplicate);
    },
    [submitWithRestrictedWordCheck, onDuplicate],
  );

  // ── Keyboard shortcut: Cmd/Ctrl+Enter to save ─────────────────

  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSubmit(onFormSubmit)();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleSubmit, onFormSubmit]);

  // ── Duration input state (allow free typing, parse on blur) ────

  const [durationInput, setDurationInput] = React.useState(
    duration && duration > 0 ? String(duration) : "",
  );

  // Sync external duration changes
  React.useEffect(() => {
    if (duration > 0) {
      setDurationInput(String(duration));
    } else {
      setDurationInput("");
    }
  }, [duration]);

  const handleDurationInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDurationInput(e.target.value);
    },
    [],
  );

  const handleDurationBlur = React.useCallback(() => {
    const parsed = parseFloat(durationInput);
    if (isNaN(parsed) || parsed <= 0) {
      const fallback = snapUnitsForMatter(0, selectedClient);
      setValue("duration", fallback);
      setDurationInput(String(fallback));
      return;
    }
    const snapped = snapUnitsForMatter(parsed, selectedClient);
    setValue("duration", snapped);
    setDurationInput(String(snapped));
  }, [durationInput, selectedClient, setValue]);

  // Live validation — clear or set the duration error as the user types
  React.useEffect(() => {
    if (durationInput === "") return;
    const parsed = parseFloat(durationInput);
    if (isNaN(parsed) || parsed <= 0) return; // blur handler will deal with it
    if (!isValidUnitsForMatter(parsed, selectedClient)) {
      setError("duration", {
        type: "manual",
        message: isZurichMatter(selectedClient) ? ZURICH_UNITS_ERROR : STANDARD_UNITS_ERROR,
      });
    } else {
      clearErrors("duration");
    }
  }, [durationInput, selectedClient, setError, clearErrors]);

  const handleDurationKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        handleDurationIncrement();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        handleDurationDecrement();
      }
    },
    [handleDurationIncrement, handleDurationDecrement],
  );

  // ── Render ─────────────────────────────────────────────────────

  return (
    <form ref={formRef} tabIndex={-1} onSubmit={handleSubmit(onFormSubmit)} className="grid gap-4 outline-none">
      {/* Document name (edit mode only) */}
      {documentName && (
        <div className="grid gap-1">
          <Label>Task Title</Label>
          <p className="text-sm text-muted-foreground leading-snug wrap-break-word rounded-md bg-muted/50 px-3 py-2">
            {documentName}
          </p>
        </div>
      )}

      {/* Matter */}
      <div className="grid w-full gap-1.5">
        <Label htmlFor="matter">Matter</Label>
        <Controller
          control={control}
          name="matter_id"
          render={({ field }) => (
            <ComboBox
              items={matters}
              value={field.value || null}
              onChange={(id) => {
                field.onChange(id);
                handleMatterSelect(id);
              }}
              fields={matterFields}
              getKey={(m) => m.matter_id}
              getDisplayLabel={(m) =>
                `${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`
              }
              getSearchValue={(m) => `${m.matter.trim()} ${m.description.trim()}`}
              renderItem={renderMatterItem}
              renderSelected={renderSelectedMatter}
              placeholder="Select matter..."
              error={errors.matter_id?.message}
              onBlur={field.onBlur}
              clearable
              onClear={() => {
                setValue("matter_id", "");
                setValue("matter", "");
              }}
            />
          )}
        />
      </div>

      {/* Task + Activity side by side */}
      <div className="grid grid-cols-2 gap-4 items-start">
        <div className="grid w-full gap-1.5">
          <Label htmlFor="task">Task</Label>
          <Controller
            control={control}
            name="task"
            render={({ field }) => (
              <ComboBox
                items={uniqueTasks}
                value={(() => {
                  if (!field.value) return null;
                  const found = uniqueTasks.find((t) => t.task === field.value);
                  return found ? `${found.task}-${found.task_description}` : null;
                })()}
                onChange={(key) => {
                  const task = uniqueTasks.find(
                    (t) => `${t.task}-${t.task_description}` === key,
                  );
                  field.onChange(task?.task ?? "");
                }}
                fields={taskFields}
                getKey={(t) => `${t.task}-${t.task_description}`}
                getDisplayLabel={(t) => `${t.task} - ${t.task_description}`}
                getSearchValue={(t) => t.task}
                renderItem={renderTaskItem}
                renderSelected={renderTaskItem}
                placeholder="Select task..."
                disabled={!taskRequired}
                disabledMessage="Not required"
                defaultLimit={taskDefaultLimit}
                clearable
                onClear={() => field.onChange("")}
                error={
                  taskRequired && errors.task?.message
                    ? errors.task.message
                    : undefined
                }
                onBlur={field.onBlur}
              />
            )}
          />
        </div>

        <div className="grid w-full gap-1.5">
          <Label htmlFor="activity">Activity</Label>
          <Controller
            control={control}
            name="activity"
            render={({ field }) => (
              <ComboBox
                items={uniqueActivities}
                value={(() => {
                  if (!field.value) return null;
                  const found = uniqueActivities.find((a) => a.activity === field.value);
                  return found ? `${found.activity}-${found.activity_description}` : null;
                })()}
                onChange={(key) => {
                  const act = uniqueActivities.find(
                    (a) => `${a.activity}-${a.activity_description}` === key,
                  );
                  field.onChange(act?.activity ?? "");
                }}
                fields={activityFields}
                getKey={(a) => `${a.activity}-${a.activity_description}`}
                getDisplayLabel={(a) => `${a.activity} - ${a.activity_description}`}
                getSearchValue={(a) => a.activity}
                renderItem={renderActivityItem}
                renderSelected={renderActivityItem}
                placeholder="Select activity..."
                disabled={!activityRequired && !isNamanHowell()}
                disabledMessage="Not required"
                defaultLimit={activityDefaultLimit}
                clearable
                onClear={() => field.onChange("")}
                error={
                  activityRequired && errors.activity?.message
                    ? errors.activity.message
                    : undefined
                }
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
      </div>

      {/* Date + Duration side by side */}
      <div className="grid grid-cols-2 gap-4 items-start">
        <div className="grid w-full gap-1.5">
          <Label htmlFor="date">Date</Label>
          <Controller
            control={control}
            name="date"
            render={({ field }) => {
              const [calOpen, setCalOpen] = React.useState(false);
              return (
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal text-foreground bg-card border-border hover:bg-muted",
                      !field.value && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {field.value
                      ? format(field.value, "PP")
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(day) => {
                      field.onChange(day || currentDate);
                      setCalOpen(false);
                    }}
                    disabled={{ after: new Date() }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              );
            }}
          />
          {errors.date && (
            <p className="text-xs text-red-500">{errors.date.message}</p>
          )}
        </div>

        <div className="grid w-full gap-1.5">
          <Label htmlFor="duration">Units</Label>
          <div className="flex gap-1">
            <Input
              id="duration"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={durationInput}
              onChange={handleDurationInputChange}
              onBlur={handleDurationBlur}
              onKeyDown={handleDurationKeyDown}
              className={cn(
                "text-foreground bg-card border-border",
                errors.duration && "border-red-500",
              )}
            />
            <div className="flex flex-col gap-0.5">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleDurationIncrement}
                className="h-[18px] w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
                tabIndex={-1}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleDurationDecrement}
                className="h-[18px] w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
                tabIndex={-1}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {errors.duration && (
            <p className="text-xs text-red-500">{errors.duration.message}</p>
          )}
        </div>
      </div>

      {/* Narrative */}
      <div className="grid w-full gap-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="description">Narrative</Label>
          <div className="flex items-center gap-2">
            {previousDescription !== null && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleUndoGeneration}
                className="flex items-center gap-1 h-8 text-xs"
              >
                <Undo2 className="h-3 w-3" />
                Undo
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateDescription}
              disabled={isGenerating || (description?.trim().length ?? 0) < 20}
              className="flex items-center gap-2 h-8 text-xs"
            >
              <Sparkles className="h-3 w-3" />
              {isGenerating ? "Generating..." : "Generate with AI"}
            </Button>
          </div>
        </div>
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <NarrativeTextArea
              ref={narrativeRef}
              id="description"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              restrictedWords={restrictedWords}
              abbreviations={abbreviations}
              error={errors.description?.message}
            />
          )}
        />
      </div>

      {/* Inline banner — blocking words take precedence over warnings */}
      {localHasBlocking && (
        <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30 px-3 py-2 animate-in fade-in-0 slide-in-from-top-1 duration-200">
          <OctagonAlert className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
          <p className="text-sm text-red-700 dark:text-red-400/80">
            Narrative contains {localMatches.filter(m => m.severity === "block").length} blocked {localMatches.filter(m => m.severity === "block").length === 1 ? "word" : "words"} that must be fixed before submitting.
          </p>
        </div>
      )}
      {!localHasBlocking && localHasWarning && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 px-3 py-2 animate-in fade-in-0 slide-in-from-top-1 duration-200">
          <TriangleAlert className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-700 dark:text-amber-400/80">
            Narrative has {localMatches.filter(m => m.severity === "warning").length} flagged {localMatches.filter(m => m.severity === "warning").length === 1 ? "word" : "words"} — review above. You can still submit.
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2">
        <div>
          {onDuplicate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleSubmit(onFormDuplicate)()}
                    className="h-10 w-10 bg-transparent border-[var(--brand-dark-green)] text-[var(--brand-dark-green)] hover:bg-[var(--brand-dark-green)]/10"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Duplicate</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <Button
              type="button"
              variant="outline"
              onClick={() => handleSubmit(onFormExport)()}
              className="bg-transparent border-[var(--brand-teal)] text-[var(--brand-teal)] hover:bg-[var(--brand-teal)]/10"
            >
              {exportLabel}
            </Button>
          )}
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="border-border text-foreground hover:bg-muted"
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            className="bg-[var(--brand-dark-green)] border-[var(--brand-dark-green)] text-white hover:bg-[var(--brand-dark-green)]/90"
          >
            {submitLabel}
          </Button>
        </div>
      </div>
      <RestrictedWordDialog
        open={restrictedWordDialogOpen}
        narrative={getValues("description")}
        date={getValues("date")}
        matterId={matterId}
        restrictedWords={restrictedWords}
        abbreviations={abbreviations}
        onBack={handleRestrictedWordCancel}
        onSave={handleRestrictedWordSave}
      />
    </form>
  );
}

export default EntryForm;
